import type { TourStep } from "../tour-steps";

/** Tours de página do domínio Laboratório — cada um roda inteiro numa única rota. */

export const CADASTROS_TOUR_STEPS: TourStep[] = [
  {
    id: "cadastros-tabs",
    route: "/lab/cadastros",
    target: '[data-tour="cadastros-tabs"]',
    title: "Um cadastro por tipo de parceiro",
    content: "Dentistas, Laboratórios, Radiologias, Rede e Fornecedores ficam separados por aba — cada tipo tem suas próprias regras de conexão.",
    skipBeacon: true,
  },
  {
    id: "cadastros-novo",
    route: "/lab/cadastros",
    target: '[data-tour="cadastros-novo"]',
    title: "Cadastre manualmente ou convide",
    content: "Já sabe os dados do parceiro? Cadastre direto por aqui. Prefere que ele mesmo preencha? Use o botão \"Gerar convite\".",
    skipBeacon: true,
    demo: "fill-form",
  },
];

export const CATALOGO_HUB_TOUR_STEPS: TourStep[] = [
  {
    id: "catalogo-hub-servicos",
    route: "/lab/catalogo",
    target: '[data-tour="catalogo-hub-servicos"]',
    title: "Serviços",
    content: "Os procedimentos protéticos que seus dentistas pedem — coroas, próteses, facetas... Clique em \"Ver Serviços\" pra ativar, desativar ou criar os seus próprios.",
    skipBeacon: true,
  },
  {
    id: "catalogo-hub-materiais",
    route: "/lab/catalogo",
    target: '[data-tour="catalogo-hub-materiais"]',
    title: "Materiais",
    content: "O que entra na fabricação — cerâmica, zircônia, resina e outros. Ative só os materiais que o seu laboratório realmente trabalha.",
    skipBeacon: true,
  },
  {
    id: "catalogo-hub-cores",
    route: "/lab/catalogo",
    target: '[data-tour="catalogo-hub-cores"]',
    title: "Cores",
    content: "Sistemas de cor (VITA Classical, 3D-Master...) que os dentistas escolhem ao montar um pedido pra você.",
    skipBeacon: true,
  },
  {
    id: "catalogo-hub-precos",
    route: "/lab/catalogo",
    target: '[data-tour="catalogo-hub-precos"]',
    title: "Preços",
    content: "Sua tabela de preços por serviço — pode ser global ou específica por cliente, clínica ou laboratório parceiro.",
    skipBeacon: true,
    demo: "list-add",
  },
];

type CatalogoGerenciarSection = "servicos" | "materiais" | "equipamentos" | "cores" | "precos";

const CATALOGO_GERENCIAR_PASSO2: Record<CatalogoGerenciarSection, Omit<TourStep, "id" | "route">> = {
  servicos: {
    target: '[data-tour="catalogo-gerenciar-secao-titulo"]',
    title: "Adicione um serviço",
    content: "Aqui ficam só os serviços que vocês oferecem. \"Adicionar serviço\", no topo, procura na base da plataforma ou cria um novo — e ele já fica disponível nos pedidos.",
    skipBeacon: true,
    demo: "fill-form",
  },
  materiais: {
    target: '[data-tour="catalogo-gerenciar-secao-titulo"]',
    title: "Monte seu cardápio",
    content:
      "Os nomes de material que o dentista escolhe ao pedir — é a chave do preço e da ficha técnica. Seus insumos e preços de compra ficam em Estoque.",
    skipBeacon: true,
    demo: "fill-form",
  },
  equipamentos: {
    target: '[data-tour="catalogo-gerenciar-secao-titulo"]',
    title: "Cadastre um equipamento",
    content:
      "Diga o que cada máquina gasta de consumível por material — é assim que a broca entra no custo do trabalho e sai do estoque.",
    skipBeacon: true,
    demo: "fill-form",
  },
  cores: {
    target: '[data-tour="catalogo-gerenciar-secao-titulo"]',
    title: "Adicione uma cor",
    content: "O botão \"Adicionar\" fica no fim da lista dessa seção — a cor já fica disponível pros seus pedidos assim que salva.",
    skipBeacon: true,
    demo: "fill-form",
  },
  precos: {
    target: '[data-tour="catalogo-gerenciar-precos-abas"]',
    title: "Catálogo Base, Tabelas ou por Clínica",
    content: "\"Catálogo Base\" é o preço padrão. Crie uma \"Tabela\" pra reusar entre vários parceiros, ou uma exceção só pra uma \"Clínica\" específica.",
    skipBeacon: true,
  },
};

/** Tour de página do Catálogo → gerenciar — passo 2 muda de acordo com a seção ativa (Serviços,
 *  Materiais, Cores ou Preços), pra sempre explicar o que a pessoa está realmente vendo em vez de
 *  forçar de volta pra Serviços. O alvo em Serviços/Materiais/Cores é o título da seção (sempre no
 *  topo), não o botão "Adicionar" — a posição dele varia com o tamanho da lista (predefinidos +
 *  itens próprios) e pode ficar fora da tela num catálogo grande, já que o tour nunca rola a
 *  página sozinho (skipScroll). Preços não tem botão "Adicionar" (fluxo é outro: escopo de
 *  tabela/cliente), então aponta pras abas de escopo, que são sempre fixas no topo. */
export function getCatalogoGerenciarTourSteps(section: CatalogoGerenciarSection): TourStep[] {
  return [
    {
      id: "catalogo-gerenciar-secoes",
      route: "/lab/catalogo/gerenciar",
      target: '[data-tour="catalogo-gerenciar-secoes"]',
      title: "Serviços, Materiais, Cores e Preços",
      content: "Use o menu (lateral no desktop, abas no celular) pra trocar de seção sem sair dessa tela.",
      skipBeacon: true,
    },
    { id: "catalogo-gerenciar-passo2", route: "/lab/catalogo/gerenciar", ...CATALOGO_GERENCIAR_PASSO2[section] },
  ];
}

export const PRODUCAO_TOUR_STEPS: TourStep[] = [
  {
    id: "producao-meus-trabalhos",
    route: "/lab/producao",
    target: '[data-tour="producao-meus-trabalhos"]',
    title: "Só o que é seu",
    content: "Ative \"Meus trabalhos\" pra ver só os pedidos sob sua responsabilidade, sem o ruído do resto da produção.",
    skipBeacon: true,
  },
  {
    id: "producao-filtros",
    route: "/lab/producao",
    target: '[data-tour="producao-filtros"]',
    title: "Filtros avançados",
    content: "Status, retrabalho, estágio interno — o painel de filtros fica recolhido por padrão, clique pra abrir.",
    skipBeacon: true,
  },
];

export const PEDIDOS_TOUR_STEPS: TourStep[] = [
  {
    id: "pedidos-tabs-status",
    route: "/lab/pedidos",
    target: '[data-tour="pedidos-tabs-status"]',
    title: "Pendentes, Recusados e Confirmados",
    content: "Recusados nunca entram em produção. Confirmados são os que você aceitou — eles alimentam a tela de Produção.",
    skipBeacon: true,
  },
  {
    id: "pedidos-visualizacao",
    route: "/lab/pedidos",
    target: '[data-tour="pedidos-visualizacao"]',
    title: "Cards ou lista",
    content: "Cards são melhores pra visualizar rápido; Lista ajuda quando você tem muitos pedidos pra comparar de uma vez.",
    skipBeacon: true,
  },
  {
    id: "pedidos-pendentes",
    route: "/lab/pedidos",
    target: '[data-tour="pedidos-pendentes"]',
    title: "Aceite ou recuse",
    content: "Clique num pedido pendente pra ver os detalhes. Aceitar manda pra produção; recusar devolve pra clínica com um motivo.",
    skipBeacon: true,
    demo: "decision",
  },
];

export const FINANCEIRO_TOUR_STEPS: TourStep[] = [
  {
    id: "financeiro-abas",
    route: "/financeiro",
    target: '[data-tour="financeiro-abas"]',
    title: "Seções do financeiro",
    content: "Visão do mês mostra o resultado; Cobranças e Contas a pagar cuidam dos extratos dos clientes e das contas; Lançamentos tem tudo que entrou e saiu; Notas fiscais guarda o registro das notas.",
    skipBeacon: true,
  },
  {
    id: "financeiro-mes",
    route: "/financeiro",
    target: '[data-tour="financeiro-mes"]',
    title: "Mês em análise",
    content: "Tudo na tela segue o mês escolhido aqui. Mês fechado vira resultado oficial: correção só por estorno.",
    skipBeacon: true,
  },
];

export const ESTOQUE_TOUR_STEPS: TourStep[] = [
  {
    id: "estoque-abas",
    route: "/lab/estoque",
    target: '[data-tour="estoque-abas"]',
    title: "Comprar, saldos, movimentos e insumos",
    content: "Comprar sugere o que repor; Saldos mostra quanto há de cada insumo; Movimentos é o extrato de um insumo; Insumos é o cadastro com custo, fornecedor e mínimo.",
    skipBeacon: true,
  },
  {
    id: "estoque-registrar-entrada",
    route: "/lab/estoque",
    target: '[data-tour="estoque-registrar-entrada"]',
    title: "Registrar entrada",
    content: "Lance aqui o que chegou do fornecedor: soma no saldo e, com o financeiro ligado, entra como saída do caixa.",
    skipBeacon: true,
  },
];
